import { faBasketShopping, faEye, faPen, faSackDollar, faScaleBalanced, faTrash, faWallet, faWeightHanging } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SaleView from "components/entities/agriculture/sale/SaleView";
import AppLink from "components/common/AppLink";
import ConfirmationModal from "components/common/confirmation/ConfirmationModal";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import HTMLCode from "components/common/HTMLCode";
import If from "components/common/If";
import Translate from "components/common/Translate";
import { graph } from "config/axios-context";
import { AppRoutes } from "config/router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteSale } from "types/agriculture/sale-types";
import { DataInfoType } from "types/datalist-types";
import { SaleDatalistAndTotalDocument, SaleDatalistAndTotalQuery, SaleDatalistAndTotalQueryVariables, SaleFilterInput, SaleSortInput, SortEnumType } from "types/graphql";
import { Roles } from "utilities/app-utilities";
import { appendFilters, DatalistFilterDefinitions, resolveSearchFilterPropertyNames } from "utilities/datalist/datalist-filter-utilities";
import { formatMoney } from "utilities/datalist/datalist-sort-utilities";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import { uuid } from "config/app-constants";
import { DatalistColumnDefinitions } from "utilities/datalist/datalist-utilities";
import Button from "components/common/button/Button";
import lodash from "lodash";
import { DeepPartial } from "@apollo/client/utilities";

type ListItem = Exclude<Exclude<Exclude<SaleDatalistAndTotalQuery['sales'], null | undefined>['items'], null | undefined>[0], null | undefined>

type DatalistTotalType = {
    total: number;
    kg: number;
    pricePerKg: number;
    cartQuantity: number;
}

export type SaleDatalistPropsType = {
    fixedFilters?: SaleFilterInput,
    hasBottomNavigation?: boolean,
    config?: DeepPartial<DatalistPropsType<ListItem, SaleFilterInput, SaleSortInput>>,
}

const SaleDatalist = (props: SaleDatalistPropsType) => {
    const [totalData, setTotalData] = useState<DatalistTotalType>({
        total: 0,
        kg: 0,
        pricePerKg: 0,
        cartQuantity: 0
    });
    const context = useAppContext();
    const datalistRef = useRef<Datalist<ListItem, SaleFilterInput, SaleSortInput>>(null);
    const [t] = useTranslation();

    const defaultDatalistConfig: DatalistPropsType<ListItem, SaleFilterInput, SaleSortInput> = {
        itemId: i => i.id,
        name: 'sale-datalist-page',
        dataSource: {
            request: async (params) => {
                let variables: SaleDatalistAndTotalQueryVariables = {
                    take: params.take,
                    skip: params.skip,
                    order: params.sort,
                    where: params.filters
                }

                if (params.searchText) {
                    const searchProps = [
                        ['person'],
                        ['category'],
                        [],
                        ['season']
                    ];
                    const searchText: SaleFilterInput = resolveSearchFilterPropertyNames(searchProps, params.searchText);
                    variables.where = appendFilters(variables.where, searchText);
                }

                if (props.fixedFilters) {
                    variables.where = appendFilters(variables.where, props.fixedFilters);
                }

                const response = await graph<SaleDatalistAndTotalQuery, SaleDatalistAndTotalQueryVariables>({
                    query: SaleDatalistAndTotalDocument,
                    variables
                });

                if (response.errors) {
                    throw response.errors
                }

                setTotalData({
                    total: response.data!.sales!.totalSum,
                    kg: response.data!.sales!.kgSum,
                    pricePerKg: response.data!.sales!.pricePerKgAverage,
                    cartQuantity: response.data!.sales!.cartQuantitySum
                });

                const res = response.data!.sales! as DataInfoType<ListItem>;
                return res;
            },
            sort: [{
                name: 'date',
                direction: SortEnumType.Desc,
                id: uuid()
            }]
        },
        table: {
            columns: [
                {
                    name: 'id',
                    resolve: i => i.id,
                    title: 'fields.id.name',
                    hide: true
                },
                {
                    name: 'date',
                    title: 'fields.date.name',
                    resolve: i => DateUtil.format(new Date(i.date), DateFormats.SHORT),
                    filter: {
                        type: 'dateOnly'
                    },
                    sort: true,
                    className: 'sticky-col'
                },
                {
                    ...DatalistColumnDefinitions.personColumnDefinition(context),
                    resolve(i) {
                        return <>
                            <span>{i.person?.name}</span>
                            <If condition={i.wallet}>
                                <span> <FontAwesomeIcon icon={faWallet} className={i.wallet?.archived ? 'text-neutral-400 dark:text-neutral-500' : ''} /></span>
                            </If>
                        </>
                    },
                },
                {
                    name: 'kg',
                    title: 'fields.kg.shortName',
                    resolve: i => formatMoney(i.kg) + ' kg',
                    filter: {
                        type: 'number'
                    },
                    sort: true
                },
                {
                    name: 'pricePerKg',
                    title: 'fields.price-per-kg.shortName',
                    filter: {
                        type: 'number'
                    },
                    sort: true,
                    resolve: i => <>{formatMoney(i.pricePerKg)} <HTMLCode code="euro" /></>
                },
                {
                    name: 'total',
                    title: 'fields.total.shortName',
                    filter: {
                        type: 'number'
                    },
                    sort: true,
                    resolve: i => <>{formatMoney(i.total)} <HTMLCode code="euro" /></>
                },
                {
                    name: 'cartQuantity',
                    title: 'fields.cart-quantity.shortName',
                    filter: {
                        type: 'number'
                    },
                    sort: true,
                    resolve: i => i.cartQuantity
                },
                {
                    name: 'category',
                    title: 'fields.category.shortName',
                    filter: {
                        type: 'reference'
                    },
                    sort: true,
                    resolve: i => t('agriculture.category.' + i.category!.name.toLowerCase())
                },
                DatalistColumnDefinitions.fruitColumnDefinition(context),
                DatalistColumnDefinitions.seasonColumnDefinition(context),
            ],
            render: {
                actions: {
                    mutationPermissionRole: Roles.AGRICULTURE_SALES_WRITE,
                    viewUrl: AppRoutes.AGRICULTURE_SALE_VIEW,
                    editUrl: AppRoutes.AGRICULTURE_SALE_EDIT
                },
                card: i => {
                    return (
                        <div key={'sales-datalist-card-' + i.id} className="w-full border theme-border px-4 py-2 mb-4 hover:border-sunrise-300">
                            <SaleView model={i as any} view="compact" name={'sale-view-datalist-view-' + i.id} />
                            <If condition={context.userHasRole(Roles.AGRICULTURE_SALES_WRITE)}>
                                <div className="grid-cols-3 grid text text-center pt-2">
                                    <div className="p-2 my-auto text-center">
                                        <AppLink className="link text-primary" to={AppRoutes.AGRICULTURE_SALE_VIEW} params={{ id: i.id }}>
                                            <FontAwesomeIcon icon={faEye} />
                                        </AppLink>
                                    </div>
                                    <div className="p-2 my-auto text-center">
                                        <AppLink className="link text-primary" to={AppRoutes.AGRICULTURE_SALE_EDIT} params={{ id: i.id }}>
                                            <FontAwesomeIcon icon={faPen} />
                                        </AppLink>
                                    </div>
                                    <div className="p-2 my-auto text-center">
                                        <Button className="link text-danger" title="button.delete" onClick={() => context.modal.open('delete', i.id)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    </div>
                                </div>
                            </If>
                            <If condition={!context.userHasRole(Roles.AGRICULTURE_SALES_WRITE)}>
                                <div className="grid-cols-1 grid text text-center pt-2">
                                    <div className="p-2 my-auto text-center">
                                        <AppLink className="link text-primary" to={AppRoutes.AGRICULTURE_SALE_VIEW} params={{ id: i.id }}>
                                            <FontAwesomeIcon icon={faEye} />
                                        </AppLink>
                                    </div>
                                </div>
                            </If>
                        </div>
                    );
                },
                footer: {
                    table: {
                        bottom: props.hasBottomNavigation ? 'bottom-app-header md:bottom-0' : '',
                        columns: [
                            {
                                name: 'total-data',
                                value: <b><Translate i18nKey="fields.total.name" /></b>,
                                className: 'sticky-col'
                            },
                            {
                                name: 'kot',
                            },
                            {
                                name: 'kg',
                                value: <b>{formatMoney(totalData.kg, 0)}</b>
                            },
                            {
                                name: 'pricePerKg',
                                value: <b>{formatMoney(totalData.pricePerKg)} <HTMLCode code="euro" /></b>
                            },
                            {
                                name: 'total',
                                value: <b>{formatMoney(totalData.total, 0)} <HTMLCode code="euro" /></b>
                            },
                            {
                                name: 'cartQuantity',
                                value: <b>{formatMoney(totalData.cartQuantity, 0)}</b>
                            },
                            {
                                name: 'kot-end',
                                colSpan: 3
                            },
                        ]
                    },
                    card: {
                        bottom: !props.hasBottomNavigation ? 'bottom-app-header-negative md:bottom-0' : '',
                        element: (
                            <div className="flex w-full text-sm">
                                <span className="w-full text-center"><FontAwesomeIcon icon={faWeightHanging} /> {formatMoney(totalData.kg, 0)} kg</span>
                                <span className="w-full text-center"><FontAwesomeIcon icon={faScaleBalanced} /> {formatMoney(totalData.pricePerKg)} <HTMLCode code="euro" /></span>
                                <span className="w-full text-center"><FontAwesomeIcon icon={faSackDollar} /> {formatMoney(totalData.total, 0)} <HTMLCode code="euro" /></span>
                                <span className="w-full text-center"><FontAwesomeIcon icon={faBasketShopping} /> {formatMoney(totalData.cartQuantity, 0)}</span>
                            </div>
                        )
                    }
                }
            }
        },
        onItemClick: {
            onClick(item, event) {
                context.navigate(AppRoutes.AGRICULTURE_SALE_VIEW, { id: item.id });
            },
            type: 'doubleClick'
        },
    }

    const datalistConfig = lodash.merge({}, defaultDatalistConfig, props.config ?? {});

    const onDelete = async () => {
        try {
            const response = await deleteSale(Number(context.modal.getData('delete')));
            if (response) context.notify('success', 'request.delete.success');
            else context.notify('error', 'request.delete.error');

            datalistRef?.current?.reload();
        } catch (error) {
            context.notify('error', 'request.delete.error');
            throw error;
        }
    }

    return (
        <>
            <Datalist ref={datalistRef}
                {...datalistConfig}
            />
            <ConfirmationModal name="delete"
                text="Delete?"
                onConfirm={onDelete}
                onReject={true}
            />
        </>
    );
};

export default SaleDatalist;
